import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';
import getTooltipStyles from '../../../../../shared/helpers';
import { setCurrencyFormat, colorsCombination } from '../../../../../utils/helpers';
import DonutChart from './DonutChart'
import AmazonChannelbarChart from '../AmazonChannelbarChart';

export const CustomTooltip = (props) => {
    // eslint-disable-next-line react/prop-types
    const { active, payload } = props;
    // we don't need to check payload[0] as there's a better prop for this purpose
    if (!active) {
        // I think returning null works based on this: http://recharts.org/en-US/examples/CustomContentOfTooltip
        return null
    }
    // mutating props directly is against react's conventions
    // so we create a new payload with the name and value fields set to what we want
    // eslint-disable-next-line react/prop-types

    const newPayload = payload && Object.values(payload).sort((a, b) => a.value < b.value ? 1 : -1)
        .map(item => ({ ...item, value: setCurrencyFormat(Number(item.value).toFixed(2), true) }))

    // we render the default, but with our overridden payload
    return <DefaultTooltipContent {...props} payload={newPayload} />;
};

const returnKey = ({ item, currentlyShow }) => {
    const { salesDay, salesMonthStr, salesYear } = item;

    let key = ''
    if (currentlyShow?.value === 1) {
        key = `${salesDay}-${salesMonthStr}-${salesYear}`;
    }

    if (currentlyShow?.value === 2) {
        key = `${salesMonthStr}-${salesYear}`;
    }

    if (currentlyShow?.value === 3) {
        key = `${salesYear}`;
    }

    return key
}


const CompareChannelWiseAreaChart = ({ dir, themeName, data, totalCounts, filteredSalesData, currentlyShow }) => {
    const dataByDay = {};

    data?.reduce(((acc, item) => {
        const key = returnKey({ item, currentlyShow })

        if (acc[key]) {
            acc[key].push(item)
        } else {
            acc[key] = [item]
        }
        return acc
    }), dataByDay);

    const dataToPlot = [];

    const channelKeyMap = {
        'Amazon.com': 'Amazon US', 'Amazon.co.uk': 'Amazon UK', 'Amazon.ca': 'Amazon CA', 'Amazon.fr': 'Amazon FR',
        'Amazon.de': 'Amazon DE', 'Amazon.it': 'Amazon IT', 'Amazon.es': 'Amazon SP',
    }

    Object.keys(dataByDay).forEach((day) => {
        const dayData = dataByDay[day];
        const item = { day };
        dayData.reduce((acc, dayItem) => {
            if (acc[channelKeyMap[dayItem.salesChannel]]) {
                acc[channelKeyMap[dayItem.salesChannel]] += Number(dayItem.amount)
            } else {
                acc[channelKeyMap[dayItem.salesChannel]] = Number(dayItem.amount)
            }
            return acc
        }, item)
        dataToPlot.push(item)
    })

    return (
        <>
            <Row className="margin-negative-30 mt-2 pt-3 " >
                <Col className="border-right container-create" sm="12" md="6"
                    style={{ width: '100%', height: '300px' }}>
                    <DonutChart totalCounts={totalCounts} channelKeyMap={channelKeyMap} isOrder title="Order Count" />
                </Col>
                <Col sm="12" md="6" style={{ width: '100%', height: '300px' }} className="container-create">
                    <DonutChart totalCounts={totalCounts} channelKeyMap={channelKeyMap} title="Revenue" />
                </Col>
                <Col sm="12" style={{ width: '100%', height: '415px' }} className="container-create pt-4" >
                    <div className="card__title mb-2">
                        <h5 className="bold-text">Amazon Revenue Comparison</h5>
                    </div>
                    <ResponsiveContainer height={340} className="dashboard__area">
                        <AreaChart data={dataToPlot} margin={{ top: 20, left: -15, bottom: 20 }}>
                            <XAxis dataKey="day" tickLine={false} reversed={dir === 'rtl'} />
                            <YAxis tickLine={false} orientation={dir === 'rtl' ? 'right' : 'left'} />
                            <Tooltip {...getTooltipStyles(themeName, 'defaultItems')} content={<CustomTooltip />} />
                            <Legend />
                            <CartesianGrid vertical={false} horizontal  stroke="#FF0000" strokeWidth={2} />
                            {Object.keys(channelKeyMap).map((channel, index) => (
                                <Area
                                    key={channel}
                                    name={channelKeyMap[channel]?.toString()}
                                    type="monotone"
                                    dataKey={channelKeyMap[channel]?.toString()}
                                    fill={colorsCombination[index % 10][0] || "#4ce1b6"}
                                    stroke={colorsCombination[index % 10][0] || "#4ce1b6"}
                                    fillOpacity={0.2}
                                />
                            ))}

                        </AreaChart>
                    </ResponsiveContainer>
                </Col>
                {Object.keys(channelKeyMap).map((channel, index) => (
                    <AmazonChannelbarChart data={filteredSalesData[channel]} channel={channelKeyMap[channel]}
                        index={index} height={300} dir={dir} returnKey={returnKey} currentlyShow={currentlyShow} />
                ))}
            </Row>

        </>
    );
};

CompareChannelWiseAreaChart.propTypes = {
    dir: PropTypes.string.isRequired,
    themeName: PropTypes.string.isRequired,
    data: PropTypes.arrayOf({}).isRequired,
    totalCounts: PropTypes.objectOf({}).isRequired,
    filteredSalesData: PropTypes.objectOf({}).isRequired,
    currentlyShow: PropTypes.objectOf({ key: "", value: "" }).isRequired

};

export default connect(state => ({ themeName: state.theme.className }))(CompareChannelWiseAreaChart);
